import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import RemoveShoppingCartOutlinedIcon from "@mui/icons-material/RemoveShoppingCartOutlined";

import { useCart } from "../../context/CartContext";
import ProductsService from "../../services/productsService";

export const ShopCart = forwardRef((props, ref) => {
  const productService = ProductsService();
  const {
    cart,
    cartLoaded,
    addItem: addItemToCart,
    removeItem: removeItemFromCart,
    removeTotalItem: removeTotalItemFromCart,
    clearCart: clearCartFromContext,
    placeOrder: placeOrderFromContext,
  } = useCart();

  const [isLoading, setIsLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [images, setImages] = useState({});
  const [purchaseOrderNo, setPurchaseOrderNo] = useState("");

  useImperativeHandle(ref, () => ({
    async addProductToCart(id, quantity) {
      await addItemToCart(id, quantity);
    },
  }));

  useEffect(() => {
    const fetchImages = async () => {
      const imgs = {};
      await Promise.all(
        cart.items.map((item) =>
          item.productId
            ? productService
                .getProductImages(item.productId)
                .then((img) => {
                  imgs[item.productId] = img;
                })
                .catch(() => {})
            : Promise.resolve()
        )
      );
      setImages(imgs);
    };
    if (cart.items.length > 0) fetchImages();
  }, [cart]);

  const handleAdd = (id) => addItemToCart(id);
  const handleRemoveOne = (item) => removeItemFromCart(item);
  const handleRemoveAll = (item) => removeTotalItemFromCart(item);
  const handleClear = () => clearCartFromContext();

  const handlePlaceOrder = async () => {
    setIsLoading(true);
    try {
      const result = await placeOrderFromContext({ purchaseOrderNo });
      if (result.success) {
        setOrderPlaced(true);
        setPurchaseOrderNo("");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const empty = cart.items.length === 0;

  return (
    <Card
      sx={{
        p: 2.5,
        position: "sticky",
        top: 80,
        border: (t) => `1px solid ${t.palette.divider}`,
        boxShadow: "none",
      }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="baseline" justifyContent="space-between">
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
            Basket
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {cart.items.length} {cart.items.length === 1 ? "item" : "items"}
          </Typography>
        </Stack>

        {/* Total */}
        <Box
          sx={{
            p: 1.5,
            bgcolor: "background.default",
            borderRadius: 1.5,
            border: (t) => `1px solid ${t.palette.divider}`,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Order total
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "primary.main" }}>
            {cart.totalPrice}
          </Typography>
        </Box>

        {/* PO number */}
        <TextField
          label="Purchase order # (optional)"
          size="small"
          fullWidth
          value={purchaseOrderNo}
          onChange={(e) => setPurchaseOrderNo(e.target.value.slice(0, 20))}
          disabled={isLoading || empty}
          inputProps={{ maxLength: 20 }}
          helperText={
            purchaseOrderNo
              ? `${purchaseOrderNo.length}/20`
              : "Reference for your records (max 20 chars)"
          }
        />

        {/* Actions */}
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="inherit"
            fullWidth
            startIcon={<RemoveShoppingCartOutlinedIcon />}
            disabled={isLoading || empty}
            onClick={handleClear}
            sx={{ color: "text.secondary", borderColor: "divider" }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={
              isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckCircleOutlineOutlinedIcon />
              )
            }
            disabled={isLoading || empty}
            onClick={handlePlaceOrder}
          >
            Place order
          </Button>
        </Stack>

        {orderPlaced && (
          <Alert
            severity="success"
            onClose={() => setOrderPlaced(false)}
            sx={{ borderRadius: 1.5 }}
          >
            Order placed successfully!
          </Alert>
        )}

        <Divider />

        {/* Items */}
        {!cartLoaded ? (
          <Stack spacing={1}>
            {[0, 1].map((i) => (
              <Skeleton key={i} variant="rounded" height={72} animation="wave" />
            ))}
          </Stack>
        ) : empty ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 2 }}
          >
            Your basket is empty
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {cart.items.map((item) => (
              <Box
                key={item.productId}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  alignItems: "center",
                  p: 1,
                  borderRadius: 1.5,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  "&:hover": { borderColor: "text.disabled" },
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: "background.default",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {images[item.productId] ? (
                    <Box
                      component="img"
                      src={`data:image/jpeg;base64,${images[item.productId]}`}
                      alt={item.description || item.productName}
                      sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <Skeleton variant="rectangular" width="100%" height="100%" />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={item.description || item.productName}
                  >
                    {item.description || item.productName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.productId}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveOne(item)}
                      disabled={isLoading}
                      sx={{ border: (t) => `1px solid ${t.palette.divider}` }}
                    >
                      <RemoveOutlinedIcon fontSize="inherit" />
                    </IconButton>
                    <Typography
                      sx={{
                        minWidth: 24,
                        textAlign: "center",
                        fontWeight: 700,
                        fontSize: 14,
                      }}
                    >
                      {item.quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleAdd(item.productId)}
                      disabled={isLoading}
                      sx={{ border: (t) => `1px solid ${t.palette.divider}` }}
                    >
                      <AddOutlinedIcon fontSize="inherit" />
                    </IconButton>
                    <Box sx={{ flex: 1 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                      {item.total || item.totalPrice}
                    </Typography>
                  </Stack>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveAll(item)}
                  disabled={isLoading}
                  sx={{ color: "error.main" }}
                  aria-label="Remove item"
                >
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
});
